import React, { MouseEventHandler, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { upToMedium } from '../../styles/breakpoints';
import HBButton from '../HBButton/HBButton';
import { MustIncludeOneOfBasicTestIdProps } from './internal/test-id.types';
import { useDetectScroll } from '../../hooks/useDetectScroll';

export interface BasicActionButtonsProps {
    primaryAction?: ActionButtonProps;
    secondaryAction?: ActionButtonProps;
}

export interface ActionButtonsProps extends BasicActionButtonsProps {
    align: 'start' | 'center' | 'end';
    sticky?: 'top' | 'bottom';
}

type ActionButtonProps = Partial<MustIncludeOneOfBasicTestIdProps> & {
    text: string;
    loading?: boolean;
    onClick: MouseEventHandler<HTMLButtonElement>;
    icon?: ReactNode;
    iconPosition?: 'start' | 'end';
};

interface Props extends ActionButtonsProps {
    containerRef?: React.RefObject<HTMLElement> | HTMLElement;
}

function ActionButtons({
    align,
    sticky,
    primaryAction,
    secondaryAction,
    containerRef
}: Props) {
    const { isScrolledToEnd } = useDetectScroll({ elementRef: containerRef });

    if (!primaryAction && !secondaryAction) {
        return null;
    }

    const secondaryActionJsx = secondaryAction && (
        <HBButton
            buttonTheme="secondary-invisible-primary"
            onClick={secondaryAction.onClick}
            testId={secondaryAction.testId}
            testIdName={secondaryAction.testIdName}
            loading={secondaryAction.loading}
            icon={secondaryAction.icon}
            iconPosition={secondaryAction.iconPosition}
        >
            {secondaryAction.text}
        </HBButton>
    );

    const isAlignStart = align === 'start';

    return (
        <Container
            $align={align}
            $sticky={sticky}
            $isScrolledToEnd={isScrolledToEnd}
        >
            {!isAlignStart && secondaryActionJsx}
            {primaryAction && (
                <HBButton
                    onClick={primaryAction.onClick}
                    testId={primaryAction.testId}
                    testIdName={primaryAction.testIdName}
                    loading={primaryAction.loading}
                    icon={primaryAction.icon}
                    iconPosition={primaryAction.iconPosition}
                >
                    {primaryAction.text}
                </HBButton>
            )}
            {isAlignStart && secondaryActionJsx}
        </Container>
    );
}

export default ActionButtons;

// -------------------------- Styles -------------------------- //

const Container = styled.div<{
    $align: ActionButtonsProps['align'];
    $sticky: ActionButtonsProps['sticky'];
    $isScrolledToEnd: boolean;
}>`
    width: 100%;
    display: flex;
    gap: 12px;
    ${upToMedium} {
        > * {
            flex: 1;
        }
    }
    ${({ $align, $sticky, $isScrolledToEnd }) => css`
        flex-wrap: ${$align === 'start' ? 'wrap' : 'wrap-reverse'};
        justify-content: ${$align === 'start'
            ? 'flex-start'
            : $align === 'center'
            ? 'center'
            : 'flex-end'};
        ${$sticky &&
        css`
            position: sticky;
            background: white;
            z-index: 1;
            ${!$isScrolledToEnd &&
            css`
                box-shadow: 0 -2px 4px 0 rgba(0, 0, 0, 0.1);
                top: 0;
            `}
        `}
        ${$sticky === 'top' &&
        css`
            top: 0;
        `}
        ${$sticky === 'bottom' &&
        css`
            bottom: 0;
            padding: 16px;
        `}
    `}
`;
